/**
 * ApiDocsPage — embeds the FastAPI Swagger UI under /api/docs plus the
 * filtered reseller view under /api/reseller-docs. Operators can:
 *   - Browse + try every admin endpoint live from the UI (full surface)
 *   - Copy a link to the reseller-only spec to hand to partners
 *
 * Why an embed instead of a custom doc generator: FastAPI already keeps
 * the spec 100% in-sync with the live router, so re-implementing the
 * docs would just drift. The Swagger UI is pinned to the same backend
 * cookie auth so "Try it" calls work without re-login.
 */
import React, { useState } from "react";
import { FileCode2, ExternalLink, Copy, Check } from "lucide-react";
import { PageHeader } from "@/components/Atoms";

const VIEWS = {
    full: {
        label: "Admin (full API)",
        docs: "/api/docs",
        openapi: "/api/openapi.json",
        description:
            "All endpoints — auth, clouds, resellers, infra (Proxmox, SDN, " +
            "IP-pools, host-networks, service-VMs, LB-routes), audit, " +
            "billing, jobs, agents, OCC. Requires superadmin cookie.",
    },
    reseller: {
        label: "Reseller (scoped)",
        docs: "/api/reseller-docs",
        openapi: "/api/reseller-openapi.json",
        description:
            "Bearer-token surface for resellers — cloud CRUD, services " +
            "(Collabora, Talk-HPB, Notify-Push, Whiteboard), domain " +
            "management, OCC. No infra / no global admin endpoints. Auth: " +
            "Authorization: Bearer cm_live_<token>",
    },
};

export default function ApiDocsPage() {
    const [view, setView] = useState("full");
    const [copied, setCopied] = useState(null);
    const cur = VIEWS[view];
    const origin =
        typeof window !== "undefined" ? window.location.origin : "";

    const copy = async (key, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-4" data-testid="api-docs-page">
            <PageHeader
                testid="api-docs-header"
                title={
                    <span className="flex items-center gap-3">
                        <FileCode2 size={20} className="text-cm-brand" />
                        API Documentation
                    </span>
                }
                subtitle="Live, auto-generated from the FastAPI router. Every endpoint below is the real backend route — &quot;Try it out&quot; calls hit the live API with your current admin cookie."
            />

            {/* View switcher */}
            <div className="flex flex-wrap gap-2" data-testid="api-docs-switcher">
                {Object.entries(VIEWS).map(([k, v]) => (
                    <button
                        key={k}
                        onClick={() => setView(k)}
                        data-testid={`api-docs-tab-${k}`}
                        className={[
                            "px-3 py-1.5 text-xs font-medium border transition-colors",
                            view === k
                                ? "border-[#CCFF00] bg-[#CCFF00]/15 text-zinc-50"
                                : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700",
                        ].join(" ")}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {/* Description + share links */}
            <div className="cm-card p-4 space-y-3 border border-zinc-800 bg-zinc-950">
                <div className="text-xs text-zinc-300 leading-relaxed">
                    {cur.description}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <ShareRow
                        label="Swagger UI"
                        url={`${origin}${cur.docs}`}
                        copied={copied === "docs"}
                        onCopy={() => copy("docs", `${origin}${cur.docs}`)}
                        testid="api-docs-link-docs"
                    />
                    <ShareRow
                        label="OpenAPI spec (JSON)"
                        url={`${origin}${cur.openapi}`}
                        copied={copied === "openapi"}
                        onCopy={() => copy("openapi", `${origin}${cur.openapi}`)}
                        testid="api-docs-link-openapi"
                    />
                </div>
                {view === "reseller" && (
                    <div className="mt-2 text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded p-2 font-mono leading-relaxed">
                        The reseller spec above is <strong>safe to share</strong> —
                        it only contains paths under <code>/api/v1/</code> that
                        a Bearer token can call. Hand the OpenAPI JSON link to
                        partners so they can import it into Postman / Bruno /
                        their OpenAPI codegen.
                    </div>
                )}
            </div>

            {/* Embedded Swagger UI */}
            <div className="border border-cm-line2 bg-white">
                <iframe
                    key={view}
                    src={cur.docs}
                    title={`API ${cur.label}`}
                    className="w-full"
                    style={{ height: "calc(100vh - 320px)", minHeight: 600 }}
                    data-testid={`api-docs-iframe-${view}`}
                />
            </div>
        </div>
    );
}

function ShareRow({ label, url, copied, onCopy, testid }) {
    return (
        <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 w-28 shrink-0">
                {label}
            </div>
            <code
                className="text-xs text-zinc-200 font-mono flex-1 truncate"
                title={url}
            >
                {url}
            </code>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-zinc-100 p-1"
                title="Open in new tab"
                data-testid={`${testid}-open`}
            >
                <ExternalLink size={12} />
            </a>
            <button
                onClick={onCopy}
                className="text-zinc-400 hover:text-zinc-100 p-1"
                title="Copy URL"
                data-testid={`${testid}-copy`}
            >
                {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            </button>
        </div>
    );
}
